import { generatePath } from 'react-router';
import { makeObservable, observable } from 'mobx';
import { isNumber } from '@cutwise/utils';

import { NOT_FOUND_PAGE } from '@/common/constants/pages';
import { assignModel } from '@/common/helpers/assignModel';
import URLProvider from '@/common/services/URLProvider';
import { DriveItemPreview } from '@/drive/entities/DriveItem/DriveItemPreview';
import { DriveItemId } from '@/drive/entities/DriveItemId';
import { PinnedItem } from '@/forwarding/entities/PinnedItem/PinnedItem';
import { PinnedProduct } from '@/forwarding/entities/PinnedItem/PinnedProduct';
import { BaseProductType, ProductType } from '@/product/constants/productTypes';
import { DiamondKind } from '@/product/entity/Diamond';
import { ProductId } from '@/product/entity/Product';
import { GlobalAccessLevel } from '@/security/constants/GlobalAccessLevel';
import { GlobalAccessTarget } from '@/security/entities/GlobalAccessTarget';
import { ProductDataAccess } from '@/security/types/DataAccess';

import { BaseDriveItem, DriveItemType } from './DriveItem.types';

export class ProductItemModel {
  id!: DriveItemId;
  title!: string;
  type!: DriveItemType.Product;
  description?: string;
  preview?: Nullable<DriveItemPreview>;
  canEdit!: boolean;
  canManageAccess!: boolean;
  productId?: ProductId;
  productType?: BaseProductType;
  hasCBR!: boolean;
  kind?: Nullable<DiamondKind>;
  labGrown?: Nullable<boolean>;
  access?: Nullable<GlobalAccessLevel>;
  dataAccess?: Nullable<ProductDataAccess>;
}

export class ProductItem extends BaseDriveItem {
  productId?: ProductId;
  productType?: BaseProductType;
  hasCBR!: boolean;
  kind?: Nullable<DiamondKind>;
  labGrown?: Nullable<boolean>;
  access?: Nullable<GlobalAccessLevel>;
  dataAccess?: Nullable<ProductDataAccess>;

  constructor(model: ProductItemModel) {
    super();

    makeObservable(this, {
      productId: observable,
      productType: observable,
      hasCBR: observable,
      kind: observable,
      labGrown: observable,
      access: observable,
      dataAccess: observable,
    });

    assignModel<ProductItemModel>(this as unknown as ProductItemModel, model);
  }

  getLinkData() {
    return {
      isExternal: false,
      url:
        this.productType && this.productId
          ? URLProvider.productDetailPage({ id: this.productId, type: this.productType })
          : generatePath(NOT_FOUND_PAGE),
    };
  }

  override canBeShared(): boolean {
    return !!this.productId;
  }

  override toPinnedItem(): Nullable<PinnedItem> {
    if (!this.productId || !this.productType) {
      return null;
    }

    return new PinnedProduct({ id: this.productId, type: this.productType });
  }

  override toGlobalAccessTarget(): Nullable<GlobalAccessTarget> {
    if (!this.productId || !isNumber(this.access)) {
      return null;
    }

    return new GlobalAccessTarget({
      id: this.productId,
      access: this.access,
      dataAccess: this.dataAccess,
      hasDataAccess: this.productType === ProductType.Diamond,
    });
  }
}
